import { createEffect, onCleanup, If, onMount, location } from 'cdui-js';

import './style.css';
import { initProcess, pState, primeOpenProgressShell, retryInitProcess, ensureOpenProgressAppBackHandler, pushVisit } from './store';

import { trackAccountOpenStart, trackAccountOpenSubmit } from '../../../../analytics';
import { submitApply } from '../../../../api/open-account';
import i18n from '../../../../i18n';
import { postToApp } from '../../../../lib/rnBridge';
import { isH5User } from '../../../../stores/user';
import { gotoLogin } from '../../utils/gotoLogin';
import { H5OpenFlowExitBar } from '../H5OpenFlowExitBar';
import { completeKycSubmitStep } from './kycAnalytics';
import { CheckMethod } from './CheckMethod';
import { OnLine } from './on-line';
import { OffLine } from './off-line';
import { EditErrorInfo } from './on-line/transfer/EditErrorInfo';
import { RiskInfo } from './on-line/risk/RiskInfo';
import { Signature } from './on-line/transfer/Signature';
import { LastNextBarPortal } from './components/LastNextBtns';
import {
  ANDROID_VIEWPORT_LOCK_MIN,
  detectKeyboardCapability,
  isAndroidWebViewKeyboardUnreliable,
} from './keyboardCapability';
import { attachOpenProgressViewportLock } from './viewportLock';
import tipsIcon from '../../../../assets/common/tips.svg';

/** 现场开户提示页：返回 App 开户页（H5 兜底回账户页） */
const leaveOnSiteWitness = () => {
  if ((window as any).ReactNativeWebView) {
    postToApp({ type: 'WEB_TO_APP_GoBack' });
    return;
  }
  location.routeTo('/account');
};

let h5OpenStartTracked = false;
/** 开户页键盘环境日志只打一次，避免 HMR / 重挂重复刷屏 */
let keyboardEnvLogged = false;

/**
 * editRefused done 提交锁（模块级）：防 createEffect 重跑 / 路由重挂导致 apply/submit 双打。
 * 离开 done（回退到 sign/risk/forms 或退出纠错）时解锁，允许下一轮纠错再提交。
 */
let editRefusedDoneSubmitLock = false;

/** 后端「申请已提交，请不要重复提交」类：视为已成功 */
const isAlreadySubmittedError = (err: unknown) => {
  const msg = typeof err === 'string' ? err : String((err as any)?.message ?? err ?? '');
  return msg.includes('不要重复提交') || msg.includes('申请已提交');
};

export const OpenProcess = () => {
  // 首帧即按 URL 挂壳（深链 step / editRefused），勿等 onMount → getByUserId
  primeOpenProgressShell();
  ensureOpenProgressAppBackHandler();

  let detachViewportLock: (() => void) | null = null;

  // 仅挂载时启动一次：避免重渲染重复 init；与 OpenAccount Bootstrap 单例闸门配合防双拉数
  onMount(() => {
    // 仅开户流程：输出系统 / 浏览器(WebView) 版本与键盘能力判定，便于真机排查
    if (!keyboardEnvLogged && typeof navigator !== 'undefined') {
      keyboardEnvLogged = true;
      const ua = navigator.userAgent || '';
      const cap = detectKeyboardCapability();
      const androidOs = (ua.match(/Android ([\d.]+)/i) || [])[1] ?? null;
      const chromeFull = (ua.match(/Chrome\/([\d.]+)/) || [])[1] ?? null;
      const iosOs =
        (ua.match(/OS (\d+[_.]\d+(?:[_.]\d+)?)/) || ua.match(/Version\/([\d.]+)/) || [])[1] ?? null;
      console.log('[OpenProgress][KeyboardEnv]', {
        ua,
        os: cap.os,
        parsedVersion: cap.version,
        mode: cap.mode,
        hasVisualViewport: cap.hasVisualViewport,
        visualViewportHeight: window.visualViewport?.height ?? null,
        innerHeight: window.innerHeight,
        androidOs,
        iosOs,
        chromeFull,
        androidUnreliable: isAndroidWebViewKeyboardUnreliable(),
        androidLockMin: ANDROID_VIEWPORT_LOCK_MIN,
      });
    }

    void initProcess();
    // 独立浏览器进入开户；App WebView 内由原生 account_open_start（helper 内自跳过）
    if (!h5OpenStartTracked) {
      h5OpenStartTracked = true;
      trackAccountOpenStart({ entry: 'h5_web' });
    }
  });

  // done 阶段：提交重审申请；成功直接回 App；失败单按钮确认后再回 App
  createEffect(() => {
    const mode = pState.editRefusedMode;
    const phase = pState.editRefusedPhase;
    if (!mode || phase !== 'done') {
      editRefusedDoneSubmitLock = false;
      return;
    }
    if (editRefusedDoneSubmitLock) return;
    editRefusedDoneSubmitLock = true;

    void (async () => {
      try {
        // 静默：重复提交类错误由下方当成功处理，避免拦截器先弹「请不要重复提交」
        await submitApply(
          { id: pState.id, progress: 20 },
          { headers: { 'X-No-Toast': '1' } },
        );
        if (pState.auditInfo) pState.auditInfo.status = 'SUBMITTED';
        trackAccountOpenSubmit();
        completeKycSubmitStep(true);
        const { showSubmitSuccessModal } = await import('./showSubmitSuccessModal');
        showSubmitSuccessModal();
      } catch (e) {
        if (isAlreadySubmittedError(e)) {
          if (pState.auditInfo) pState.auditInfo.status = 'SUBMITTED';
          trackAccountOpenSubmit();
          completeKycSubmitStep(true);
          const { showSubmitSuccessModal } = await import('./showSubmitSuccessModal');
          showSubmitSuccessModal();
          return;
        }
        trackAccountOpenSubmit();
        completeKycSubmitStep(false);
        const { showSubmitFailThenFinish } = await import('./showSubmitSuccessModal');
        showSubmitFailThenFinish(e);
      }
    })();
  });

  const Comp = () => {
    console.log('[OpenProgress][Comp]', {
      current: pState.current,
      applyMethod: pState.applyMethod,
      status: pState.status,
      shellReady: pState.shellReady,
      loading: pState.loading,
      editRefusedMode: pState.editRefusedMode,
      queryStep: location.query.step,
    });
    // 现场开户：不走线上/预约流程，仅提示前往网点
    if (pState.applyMethod === 'ON_SITE_WITNESS') {
      console.log('[OpenProgress][Comp] → OnSiteWitness');
      return (
        <div class="flex flex-column" style={{ height: '100%', 'min-height': 0 }}>
          <div class="flex flex-column align-center padding-m-x-L padding-m-t-L margin-t-XL">
            <img src={tipsIcon} alt="" width="60" height="60" />
            <div class="color-D font-s-L text-center margin-t-L font-weight-bold" style={{ 'line-height': '28px' }}>
              {i18n.OpenProgress.Entry.OnSiteWitnessTip}
            </div>
          </div>
          <div style={{ flex: 1, 'min-height': 0 }} />
          <div class="page-foot padding-m-x-L padding-m-y-S">
            <button
              type="button"
              class="primary-btn btn-rounded width-100"
              style={{ height: '50px' }}
              onclick={leaveOnSiteWitness}
            >
              {i18n.OpenProgress.Common.Back}
            </button>
          </div>
        </div>
      );
    }
    if (pState.current === 0) {
      console.log('[OpenProgress][Comp] → CheckMethod');
      return <CheckMethod />;
    }
    if (pState.applyMethod === 'ONLINE_TRANSFER') {
      console.log('[OpenProgress][Comp] → OnLine');
      return <OnLine />;
    }
    if (pState.applyMethod === 'RESERVATION') {
      console.log('[OpenProgress][Comp] → OffLine');
      return <OffLine />;
    }
    console.warn('[OpenProgress][Comp] → UnderDevelopment (未知 applyMethod)', {
      current: pState.current,
      applyMethod: pState.applyMethod,
      applyMethodType: typeof pState.applyMethod,
    });
    return <div>{i18n.OpenProgress.Entry.UnderDevelopment}</div>;
  };

  onCleanup(() => {
    detachViewportLock?.();
    detachViewportLock = null;
  });

  return (
    <div
      class="open-progress color"
      ref={(el: HTMLDivElement | null) => {
        detachViewportLock?.();
        detachViewportLock = null;
        if (el) detachViewportLock = attachOpenProgressViewportLock(el);
      }}
    >
      <H5OpenFlowExitBar />
      <div class="app-open-progress-content bg-c">
        {pState.loadError ? (
          <div
            class="flex flex-column align-center justify-center open-progress-load-error"
            style={{ height: '100%', gap: '16px', 'padding-top': '200px' }}
          >
            <div class="color-D font-s-L text-align-center">
              {pState.loadErrorMessage || i18n.OpenProgress.Entry.LoadFailed}
            </div>
            <button
              class="primary-btn btn-rounded"
              style={{ width: '200px', height: '50px' }}
              onclick={() => retryInitProcess()}
            >
              {i18n.OpenProgress.Entry.Retry}
            </button>
            <If when={isH5User()}>
              <button
                class="default-btn btn-rounded"
                style={{ width: '200px', height: '50px' }}
                onclick={() => gotoLogin()}
              >
                {i18n.OpenProgress.Entry.GoLogin}
              </button>
            </If>
          </div>
        ) : pState.applyReady ? (
          <>
            {/**
             * 须等 getByUserId + finishInit（applyReady）后再挂业务分支，
             * 避免默认 applyMethod=ONLINE_TRANSFER / current=0 先闪 CheckMethod 再切现场开户等。
             *
             * editRefusedMode 使用 <If> 确保相位变化后 JSX 层能追踪到响应式更新。
             * 普通 if 语句在 cdui-js 组件函数体里是"一次性"求值，不会建立依赖。
             * <If when={expr}> 内部通过 getter 持续追踪，相位变化时会正确 mount/unmount。
             */}
            <If when={pState.editRefusedMode}>
              <If when={pState.editRefusedPhase === 'forms'}>
                {/*
                  App 的 APP_TO_WEB_EditOpinion / getByUserId 可能晚于壳挂载才写入 errorValues。
                  等有 opinion 再挂 EditErrorInfo，避免空 list 一次性缓存导致白屏。
                */}
                <If when={pState.errorValues.length > 0}>
                  <EditErrorInfo
                    onComplete={() => {
                      const mainland = pState.uploadLicForm.idCardType === 'MAINLAND_CHINA';
                      if (mainland) {
                        // 仅清内存，强制重走 CA；localStorage 保留至 finishOpenAccount
                        pState.certSn = '';
                        pushVisit({ step: pState.current, editRefusedPhase: 'risk' });
                      } else {
                        pushVisit({ step: pState.current, editRefusedPhase: 'sign' });
                      }
                    }}
                    close={() => {
                      /* 首步无处可退，忽略 */
                    }}
                  />
                </If>
              </If>

              <If when={pState.editRefusedPhase === 'risk'}>
                <RiskInfo />
              </If>

              <If when={pState.editRefusedPhase === 'sign'}>
                <Signature />
              </If>

              {/* done → createEffect 里提交 + FINISHOPEN；此处仅展示过渡 UI */}
              <If when={pState.editRefusedPhase === 'done'}>
                <div class="flex flex-column align-center justify-center" style={{ height: '100%', gap: '16px' }}>
                  <div class="color-D font-s-L">{i18n.OpenProgress.Entry.Submitting}</div>
                </div>
              </If>
            </If>
            {/* 正常开户流程（editRefusedMode 为 false 时） */}
            <If when={!pState.editRefusedMode}>{Comp()}</If>
          </>
        ) : null}
      </div>
      {/* 底栏 Portal：挂在顶层、滚动容器之外，position: fixed 相对视口生效（修复安卓底栏随滚动跳动） */}
      <LastNextBarPortal />
    </div>
  );
};
